// === src/pages/dashboard/Vehicles.jsx ===
import React, { useState, useEffect, useRef } from "react";
import Loader from "../../components/Loader";
import { getAllVehicles, addVehicle, updateVehicle, deleteVehicle } from "../../services/api";
import { toast } from "react-toastify";
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, LabelList,
  BarChart, Bar, XAxis, YAxis,
  LineChart, Line,
  ResponsiveContainer
} from "recharts";

const fieldLabels = {
  vin: "VIN",
  licensePlate: "License Plate",
  make: "Make",
  model: "Model",
  year: "Year",
  registrationExpiryDate: "Registration Expiry Date",
  insuranceExpiryDate: "Insurance Expiry Date",
  odometer: "Odometer",
  status: "Status",
  diagnosticReport: "Diagnostic Report"
};

const STATUS_COLORS = { Active: '#f97316', "In Repair": '#10b981', Idle: '#facc15' };
const editableFields = Object.keys(fieldLabels);
const addableFields = editableFields.filter(f => f !== "status" && f !== "diagnosticReport");

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingVin, setEditingVin] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState(Object.fromEntries(addableFields.map(f => [f, ""])));
  const inputRef = useRef(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const res = await getAllVehicles();
      setVehicles(res);
    } catch {
      toast.error("Failed to fetch vehicles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editingVin && inputRef.current) inputRef.current.focus();
  }, [editingVin]);

  const handleInputChange = (e, setter) => {
    const { name, value } = e.target;
    setter(prev => ({ ...prev, [name]: value }));
  };

  const handleInlineSave = async () => {
    if (!editRow.vin || !editRow.licensePlate) {
      toast.warning("VIN and License Plate are required");
      return;
    }
    setLoading(true);
    try {
      const updated = {
        ...editRow,
        registrationExpiryDate: new Date(editRow.registrationExpiryDate).toISOString(),
        insuranceExpiryDate: new Date(editRow.insuranceExpiryDate).toISOString()
      };
      await updateVehicle(editingVin, updated);
      setEditingVin(null);
      setEditRow({});
      await fetchVehicles();
      toast.success("Vehicle updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    if (!newVehicle.vin || !newVehicle.licensePlate) {
      toast.warning("VIN and License Plate are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...newVehicle,
        registrationExpiryDate: new Date(newVehicle.registrationExpiryDate).toISOString(),
        insuranceExpiryDate: new Date(newVehicle.insuranceExpiryDate).toISOString()
      };
      await addVehicle(payload);
      setShowAddForm(false);
      setNewVehicle(Object.fromEntries(addableFields.map(f => [f, ""])));
      await fetchVehicles();
      toast.success("Vehicle added");
    } catch {
      toast.error("Add failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vin) => {
    if (!window.confirm("Delete this vehicle?")) return;
    setLoading(true);
    try {
      await deleteVehicle(vin);
      await fetchVehicles();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const statusData = [
    { name: 'Active', value: 98 },
    { name: 'In Repair', value: 12 },
    { name: 'Idle', value: 14 }
  ];

  const tripsData = vehicles.map((v, i) => ({ vin: v.vin, trips: (i * 7 + 10) % 40 + 10 }));
  const fuelData = vehicles.map((v, i) => ({ vin: v.vin, fuel: (i * 3 + 15) % 30 + 10 }));

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Fleet Vehicles</h2>

      <button
        onClick={() => setShowAddForm(prev => !prev)}
        className="mb-4 bg-orange-500 text-white px-4 py-2 rounded shadow"
      >
        {showAddForm ? "Cancel Add Vehicle" : "+ Add Vehicle"}
      </button>

      {showAddForm && (
        <div className="mb-6 p-4 border rounded bg-white dark:bg-gray-800 shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addableFields.map((field) => (
              <div key={field} className="flex flex-col">
                <label className="text-sm font-medium mb-1">{fieldLabels[field]}</label>
                <input
                  name={field}
                  value={newVehicle[field] || ""}
                  onChange={(e) => handleInputChange(e, setNewVehicle)}
                  type={field.includes("Date") ? "date" : field === "year" || field === "odometer" ? "number" : "text"}
                  className="border px-3 py-2 rounded dark:bg-gray-700"
                />
              </div>
            ))}
          </div>
          <button onClick={handleAddVehicle} className="mt-4 bg-green-600 text-white px-4 py-2 rounded shadow">
            Save Vehicle
          </button>
        </div>
      )}

      <div className="overflow-auto shadow rounded-lg mb-12">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {editableFields.map((field) => (
                <th key={field} className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-white">
                  {fieldLabels[field]}
                </th>
              ))}
              <th className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {vehicles.map((vehicle, idx) => (
              <tr key={idx} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                {editableFields.map((field, i) => (
                  <td key={field} className="px-4 py-2 whitespace-nowrap text-sm">
                    {editingVin === vehicle.vin ? (
                      <input
                        name={field}
                        value={editRow[field] || ""}
                        onChange={(e) => handleInputChange(e, setEditRow)}
                        ref={i === 0 ? inputRef : null}
                        type={field.includes("Date") ? "date" : field === "year" || field === "odometer" ? "number" : "text"}
                        className="border px-2 py-1 rounded w-full dark:bg-gray-700"
                      />
                    ) : field.includes("Date") && vehicle[field] ? (
                      new Date(vehicle[field]).toISOString().split("T")[0]
                    ) : field === "diagnosticReport" && vehicle[field] ? (
                      <a href={vehicle[field]} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">View</a>
                    ) : (
                      vehicle[field]
                    )}
                  </td>
                ))}
                <td className="px-4 py-2 whitespace-nowrap space-x-2">
                  {editingVin === vehicle.vin ? (
                    <>
                      <button onClick={handleInlineSave} className="text-green-600 hover:underline">Save</button>
                      <button onClick={() => setEditingVin(null)} className="text-gray-600 hover:underline">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingVin(vehicle.vin);
                          setEditRow(vehicle);
                        }}
                        className="text-blue-600 hover:underline"
                      >Edit</button>
                      <button onClick={() => handleDelete(vehicle.vin)} className="text-red-600 hover:underline">Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded shadow p-4">
          <h3 className="text-sm font-semibold mb-2">Vehicle Status Overview</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                animationDuration={800}
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || "#8884d8"} />
                ))}
                <LabelList dataKey="value" position="outside" />
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded shadow p-4">
          <h3 className="text-sm font-semibold mb-2">Trips This Week</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={tripsData} animationDuration={800}>
              <XAxis dataKey="vin" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Bar dataKey="trips" fill="#f97316">
                <LabelList dataKey="trips" position="top" />
              </Bar>
              <RechartsTooltip />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded shadow p-4">
          <h3 className="text-sm font-semibold mb-2">Fuel Consumption</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={fuelData} animationDuration={800}>
              <XAxis dataKey="vin" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Line type="monotone" dataKey="fuel" stroke="#10b981" strokeWidth={2} />
              <RechartsTooltip />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Vehicles;
